import { inject, Injectable } from '@angular/core';
import {
  TranscriptExtractionData,
  FieldWithBbox,
  Student,
  Course,
  Metadata
} from '../../models/transcriptExtractionData.model';
import { TranscriptType } from '../../constants';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class TranscriptMappingService {
  private readonly translationService = inject(TranslateService);

  /**
   * Maps raw backend data to structured TranscriptExtractionData format
   * @param backendData - The raw data from the backend API to be mapped to TranscriptExtractionData
   * @param transcriptType - The numerical transcript type
   * @returns Mapped transcript data with Student, Institutions, and Courses
   */
  mapToTranscriptData(backendData: any, transcriptType: TranscriptType): TranscriptExtractionData {
    const data = this.unwrapData(backendData);

    return {
      Student: this.mapStudent(data),
      Institutions: this.mapInstitutions(data, transcriptType),
      Courses: this.mapCourses(data),
      Metadata: this.mapMetadata(data)
    };
  }

  /**
   * Unwraps nested data structures to access the core transcript data
   * @param data - Raw data that may be wrapped in common container properties
   * @returns Unwrapped data object
   */
  private unwrapData(data: any): any {
    return data.data || data.result || data.transcript || data.content || data;
  }

  /**
   * Maps student information from various possible field names to standardized Student object
   * @param data - Raw data containing student information
   * @returns Mapped Student object with standardized field names
   */
  private mapStudent(data: any): Student {
    const student = this.findNestedValue(data, [
      'Student',
      'student',
      'studentData',
      'student_data',
      'studentInfo',
      'student_info'
    ]);
    return {
      FirstName: this.mapField(
        this.findNestedValue(student, ['FirstName', 'firstName', 'first_name', 'fname'])
      ),
      MiddleName: this.mapField(
        this.findNestedValue(student, ['MiddleName', 'middleName', 'middle_name', 'mname'])
      ),
      LastName: this.mapField(
        this.findNestedValue(student, ['LastName', 'lastName', 'last_name', 'lname', 'surname'])
      ),
      DateOfBirth: this.mapField(
        this.findNestedValue(student, [
          'DateOfBirth',
          'dateOfBirth',
          'dob',
          'birthDate',
          'birth_date'
        ])
      ),
      Last4SSN: this.mapField(
        this.findNestedValue(student, [
          'Last4SSN',
          'ssn',
          'socialSecurity',
          'last4ssn',
          'social_security_number'
        ])
      ),
      Email: this.mapField(this.findNestedValue(student, ['Email', 'email', 'emailAddress'])),
      StudentId: this.mapField(
        this.findNestedValue(student, ['StudentId', 'studentId', 'student_id'])
      )
    };
  }

  /**
   * Maps institution data to array of Institution objects
   * @param data - Raw data containing institution information
   * @param transcriptType - The numerical transcript type
   * @returns Array of mapped Institution objects
   */
  private mapInstitutions(
    data: any,
    transcriptType: TranscriptType
  ): Record<string, FieldWithBbox>[] {
    const institutions =
      this.findNestedValue(data, [
        'Institutions',
        'institutions',
        'institutionData',
        'institution_data',
        'schools',
        'academicInstitutions'
      ]) || [];
    const instArray = Array.isArray(institutions) ? institutions : [institutions];
    return instArray.filter(inst => inst).map(inst => this.mapInstitution(inst, transcriptType));
  }

  /**
   * Maps a single institution object to standardized Institution format
   * @param inst - Raw institution data
   * @param transcriptType - The numerical transcript type
   * @returns Mapped Institution object
   */
  private mapInstitution(inst: any, transcriptType: TranscriptType): Record<string, FieldWithBbox> {
    const sharedData: Record<string, FieldWithBbox> = {
      Name: this.mapField(
        this.findNestedValue(inst, [
          'Name',
          'name',
          'InstitutionName',
          'institutionName',
          'institution_name',
          'schoolName'
        ])
      ),
      TotalCreditsEarned: this.mapField(
        this.findNestedValue(inst, [
          'TotalCreditsEarned',
          'totalcreditsearned',
          'Totalcreditsearned',
          'totalCreditsEarned',
          'totalCredits',
          'TotalCredits',
          'totalcredits',
          'credits',
          'Credits',
          'creditsearned',
          'CreditsEarned'
        ])
      )
    };

    let institutionData: Record<string, FieldWithBbox> = {};

    switch (transcriptType) {
      case TranscriptType.HighSchool:
        institutionData = {
          CEEB: this.mapField(this.findNestedValue(inst, ['CEEB', 'ceeb', 'Ceeb'])),
          WeightedGPA: this.mapField(
            this.findNestedValue(inst, ['WeightedGPA', 'WEIGHTEDGPA', 'weightedGPA', 'weightedgpa'])
          ),
          UnweightedGPA: this.mapField(
            this.findNestedValue(inst, [
              'UnweightedGPA',
              'UnWeightedGPA',
              'UnweightedGPA',
              'unweightedgpa',
              'unWeightedGpa',
              'UnweightedGpa',
              'UnWeightedGpa'
            ])
          ),
          GPA: this.mapField(this.findNestedValue(inst, ['GPA', 'gpa'])),
          AttendanceStartDate: this.mapField(
            this.findNestedValue(inst, [
              'AttendanceStartDate',
              'attendancestartdate',
              'attendanceStartDate',
              'attendancestart',
              'attendanceStart',
              'AttendanceStart',
              'startdate',
              'startDate',
              'StartDate',
              'Start',
              'start'
            ])
          ),
          AttendanceEndDate: this.mapField(
            this.findNestedValue(inst, [
              'AttendanceEndDate',
              'attendanceenddate',
              'attendanceEndDate',
              'attendanceend',
              'attendanceEnd',
              'Attendanceend',
              'enddate',
              'endDate',
              'EndDate',
              'End',
              'end'
            ])
          ),
          AttendanceStartYear: this.mapField(
            this.findNestedValue(inst, ['AttendanceStartYear', 'attendanceStartYear'])
          ),
          AttendanceEndYear: this.mapField(
            this.findNestedValue(inst, ['AttendanceEndYear', 'attendanceEndYear'])
          ),
          GraduationDate: this.mapField(
            this.findNestedValue(inst, [
              'GraduationDate',
              'Graduation',
              'graduationDate',
              'graduationdate',
              'GradDate',
              'gradDate',
              'graddate',
              'graduation'
            ])
          ),
          GraduationStatus: this.mapField(
            this.findNestedValue(inst, [
              'GraduationStatus',
              'Status',
              'graduationStatus',
              'graduationstatus',
              'GradStatus',
              'gradStatus',
              'gradstatus',
              'status'
            ]),
            ['Graduated', 'Not Graduated']
          ),
          ClassSize: this.mapField(
            this.findNestedValue(inst, ['ClassSize', 'classsize', 'classSize', 'Size', 'size'])
          ),
          ClassRank: this.mapField(
            this.findNestedValue(inst, ['ClassRank', 'classrank', 'classRank', 'Rank', 'rank'])
          )
        };
        break;
      case TranscriptType.College:
      default:
        institutionData = {
          CumulativeGPA: this.mapField(
            this.findNestedValue(inst, ['CumulativeGPA', 'gpa', 'cumulativeGPA', 'overallGPA'])
          ),
          Accreditations: this.mapField(
            this.findNestedValue(inst, ['Accreditations', 'accreditations', 'accreditation'])
          )
        };
        break;
    }

    return {
      ...sharedData,
      ...institutionData
    };
  }

  /**
   * Maps course data to array of Course objects
   * @param data - Raw data containing course information
   * @returns Array of mapped Course objects
   */
  private mapCourses(data: any): Course[] {
    const courses = this.flattenCourses(data);
    return courses.map(course => this.mapCourse(course));
  }

  /**
   * Flattens courses from various nested structures including terms and institutions
   * @param data - Raw data that may contain courses in different locations
   * @returns Array of raw course objects with term information added
   */
  private flattenCourses(data: any): any[] {
    const directCourses = this.findNestedValue(data, ['Courses', 'courses', 'classes']);
    if (Array.isArray(directCourses)) return directCourses;

    const allCourses: any[] = [];

    const rootTerms = this.findNestedValue(data, [
      'Terms',
      'terms',
      'semesters',
      'academicHistory'
    ]);
    if (Array.isArray(rootTerms)) {
      rootTerms.forEach(term => {
        const termName = this.findNestedValue(term, ['Term', 'term', 'name', 'semester']) || '';
        const courses = this.findNestedValue(term, ['Courses', 'courses', 'classes']) || [];

        if (Array.isArray(courses)) {
          courses.forEach(course => {
            allCourses.push({
              ...course,
              Term: course.Term || termName
            });
          });
        }
      });
    }

    const institutions = this.findNestedValue(data, ['Institutions', 'institutions']) || [];
    if (Array.isArray(institutions)) {
      institutions.forEach(inst => {
        const terms = inst.Terms || inst.terms || [];
        if (Array.isArray(terms)) {
          terms.forEach(term => {
            const termName = this.findNestedValue(term, ['Term', 'term', 'name']) || '';
            const courses = this.findNestedValue(term, ['Courses', 'courses', 'classes']) || [];

            if (Array.isArray(courses)) {
              courses.forEach(course => {
                allCourses.push({
                  ...course,
                  Term: course.Term || termName
                });
              });
            }
          });
        }
      });
    }

    return allCourses;
  }

  /**
   * Maps a single course object to standardized Course format
   * @param course - Raw course data
   * @returns Mapped Course object with standardized field names
   */
  private mapCourse(course: any): Course {
    const mappedCourse = {
      Term: this.mapField(
        this.findNestedValue(course, ['Term', 'term', 'semester', 'quarter', 'Quarter'])
      ),
      StartDate: this.mapField(
        this.findNestedValue(course, ['StartDate', 'startDate', 'start_date', 'beginDate'])
      ),
      EndDate: this.mapField(
        this.findNestedValue(course, ['EndDate', 'endDate', 'end_date', 'completionDate'])
      ),
      CourseTitle: this.mapField(
        this.findNestedValue(course, ['CourseTitle', 'title', 'courseName', 'name', 'description'])
      ),
      Subject: this.mapField(
        this.findNestedValue(course, ['Subject', 'subject', 'department', 'discipline', 'prefix'])
      ),
      CourseNumber: this.mapField(
        this.findNestedValue(course, [
          'CourseNumber',
          'courseNumber',
          'number',
          'courseCode',
          'code'
        ])
      ),
      CreditsEarned: this.mapField(
        this.findNestedValue(course, [
          'CreditsEarned',
          'credits',
          'creditHours',
          'units',
          'creditValue'
        ])
      ),
      GradePoints: this.mapField(
        this.findNestedValue(course, ['GradePoints', 'gradePoints', 'points', 'qualityPoints'])
      ),
      Grade: this.mapField(
        this.findNestedValue(course, ['Grade', 'grade', 'letterGrade', 'finalGrade'])
      ),
      Institution: this.mapField(
        this.findNestedValue(course, [
          'College',
          'college',
          'Institution',
          'institution',
          'school',
          'campus',
          'Campus'
        ])
      ),
      InstitutionId: this.mapField(
        this.findNestedValue(course, ['InstitutionId', 'institutionId']) || ''
      )
    };

    const endDateString = mappedCourse.EndDate.value?.toString() ?? '';
    const startDateString = mappedCourse.StartDate.value?.toString() ?? '';

    if (isNaN(Date.parse(endDateString))) {
      mappedCourse.EndDate = { value: '', bbox: null, confidence: null, page: null };
    }

    if (isNaN(Date.parse(startDateString))) {
      mappedCourse.StartDate = { value: '', bbox: null, confidence: null, page: null };
    }

    return mappedCourse;
  }

  /**
   * Searches for a value in an object using multiple possible key names
   * @param obj - Object to search in
   * @param keys - Array of possible key names to search for
   * @returns First matching value found, or null if none found
   */
  private findNestedValue(obj: any, keys: string[]): any {
    if (!obj) return null;

    for (const key of keys) {
      if (obj[key] !== undefined && obj[key] !== null) {
        return obj[key];
      }
    }
    return null;
  }

  /**
   * Maps any field value to FieldWithBbox format with metadata
   * @param field - Raw field value that may be primitive, object, or array
   * @param availableValues - field values allowed in dropdown
   * @returns FieldWithBbox object with value and optional bbox, confidence, and page data
   */
  private mapField(field: any, availableValues?: Array<any>): FieldWithBbox {
    const type = availableValues ? 'dropdown' : 'input';
    let options = null;
    if (availableValues) {
      options = availableValues.map(val => {
        const valLabel = this.translationService.instant(
          'transcriptData.' + (val.charAt(0).toLowerCase() + val.slice(1)).replace(' ', '')
        );
        return {
          label: valLabel,
          value: val
        };
      });
    }

    if (field === null || field === undefined) {
      return {
        value: '',
        bbox: null,
        confidence: null,
        page: null,
        type: type,
        options: options
      };
    }

    if (typeof field === 'string' || typeof field === 'number') {
      let fieldValue = field;
      if (availableValues) {
        if (availableValues.includes(field)) {
          fieldValue = field;
        } else {
          fieldValue = '';
        }
      }
      return {
        value: fieldValue,
        bbox: null,
        confidence: null,
        page: null,
        type: type,
        options: options
      };
    }

    if (Array.isArray(field)) {
      const firstValue = field.find(item => item !== null && item !== undefined);
      return this.mapField(firstValue, availableValues);
    }

    if (typeof field === 'object') {
      let extractedValue = this.findNestedValue(field, ['value', 'text', 'content', 'data']);

      if (availableValues) {
        if (availableValues.includes(extractedValue)) {
          extractedValue = extractedValue;
        } else {
          extractedValue = '';
        }
      }

      return {
        value: extractedValue,
        bbox: this.findNestedValue(field, [
          'bbox',
          'boundingBox',
          'coordinates',
          'bounds',
          'coords'
        ]),
        confidence: this.findNestedValue(field, [
          'bbox_confidence',
          'confidence',
          'score',
          'probability'
        ]),
        page: this.findNestedValue(field, ['bbox_page', 'page', 'pageNumber', 'pageIndex']),
        reviewed: field.reviewed ?? false,
        type: type,
        options: options
      };
    }

    let fieldValue = field;
    if (availableValues) {
      if (availableValues.includes(field)) {
        fieldValue = field;
      } else {
        fieldValue = '';
      }
    }

    return {
      value: String(fieldValue),
      bbox: null,
      confidence: null,
      page: null,
      type: type,
      options: options
    };
  }

  /**
   * Maps metadata information from raw data to standardized Metadata object
   * @param data - Raw data containing metadata information
   * @returns Mapped Metadata object with default values if metadata is not present
   */
  private mapMetadata(data: any): Metadata {
    const metadata = this.findNestedValue(data, ['Metadata', 'metadata', 'meta']);

    if (!metadata) {
      return { ConfidenceLevel: 0, ConfidenceLevelReasoning: '' };
    }

    return {
      ConfidenceLevel:
        this.findNestedValue(metadata, ['ConfidenceLevel', 'confidenceLevel', 'confidence']) ?? 0,
      ConfidenceLevelReasoning:
        this.findNestedValue(metadata, [
          'ConfidenceLevelReasoning',
          'confidenceLevelReasoning',
          'confidenceReasoning',
          'reasoning'
        ]) ?? ''
    };
  }
}
